import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../../config/links';
import LegalFooter from '../../components/LegalFooter';
import useDocumentMeta from '../../hooks/useDocumentMeta';

/* Shared chrome for /resources/* pillar essays. Each pillar passes:
   - meta: { title, description }
   - hero: { eyebrow, h1, thesis }  // thesis is the single-line argument
   - sections: [{ heading, body: ReactNode }]   // body can include <p>, <ul>, callouts
   - articleSchema: structured-data object for JSON-LD Article (for LLM/AI extraction)
   The component renders header, hero, the numbered sections with H2s,
   a closer CTA, related-reading links, and the legal footer + modal. */
const PillarLayout = ({ meta, hero, sections, related, articleSchema }) => {

  useDocumentMeta(meta);

  /* Home → Resources → this article, so the subpage hierarchy is machine-readable. */
  const breadcrumbSchema = meta?.path && {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Factory OS', item: 'https://factory-os.com' },
      { '@type': 'ListItem', position: 2, name: 'Resources', item: 'https://factory-os.com/resources' },
      { '@type': 'ListItem', position: 3, name: articleSchema?.headline || hero?.h1, item: `https://factory-os.com${meta.path}` },
    ],
  };

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      )}
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-24 pb-10 sm:pb-12 px-5">
        <div className="max-w-[820px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          {hero.frame && (
            <p className="mb-6" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.5, borderLeft: '2px solid var(--nb-accent)', paddingLeft: 12 }}>
              {hero.frame}
            </p>
          )}
          <div className="mb-5">
            <span className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>{hero.eyebrow}</span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            {hero.h1}
          </h1>

          <p className="mt-5" style={{ fontFamily: 'var(--script)', color: 'var(--nb-accent)', fontSize: 'var(--t-md)', lineHeight: 1.2 }}>
            {hero.thesis}
          </p>
        </div>
      </section>

      {/* ══════════ BODY SECTIONS ══════════ */}
      <article className="px-5 pb-10" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          {sections.map((s, i) => (
            <section key={i} className="pt-10 pb-2">
              <div className="nb-eyebrow" style={{ color: 'var(--ink-3)' }}>{String(i + 1).padStart(2, '0')}</div>
              <h2 className="mt-1" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.15, color: 'var(--ink)' }}>
                {s.heading}
              </h2>
              <div className="mt-4 pillar-body" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.55 }}>
                {s.body}
              </div>
            </section>
          ))}
        </div>
      </article>

      {/* ══════════ CLOSER ══════════ */}
      <section className="py-12 md:py-[72px] px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[760px] mx-auto">
          <h2 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.15, color: 'var(--ink)', marginBottom: 14 }}>
            See it in the platform.
          </h2>
          <p className="mb-7" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
            The 2-minute tour walks the actual loop end to end.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>

          {related && related.length > 0 && (
            <div className="mt-10 pt-6" style={{ borderTop: '1px dashed var(--ink-4)' }}>
              <div className="nb-eyebrow" style={{ color: 'var(--ink-3)' }}>related reading</div>
              <ul className="mt-3 list-none p-0 m-0 flex flex-col gap-2">
                {related.map((r, i) => (
                  <li key={i}>
                    <a href={r.href} style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
                      → {r.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default PillarLayout;
